import { LoginRequest, LoginResponse, RefreshTokenRequest, RefreshTokenResponse, User } from "../../model/Model";
import { get, post } from "../api";

const ACCESS_TOKEN_KEY = 'geslocative_access_token';
const REFRESH_TOKEN_KEY = 'geslocative_refresh_token';
const TOKEN_EXPIRY_KEY = 'geslocative_token_expiry';
const REFRESH_EXPIRY_KEY = 'geslocative_refresh_expiry';

export async function login(email: string, password: string): Promise<LoginResponse> {
    try {
        const loginRequest: LoginRequest = { email, password };
        const response = await post('/auth/login', loginRequest);
        
        // Stocker uniquement les tokens (pas l'utilisateur)
        if (response.data.accessToken && response.data.refreshToken) {
            const expiryTime = Date.now() + (response.data.expiresIn || 900000); // 15 min par défaut
            const refreshExpiryTime = Date.now() + (7 * 24 * 60 * 60 * 1000); // 7 jours par défaut
            
            // Access token dans sessionStorage (plus sécurisé)
            sessionStorage.setItem(ACCESS_TOKEN_KEY, response.data.accessToken);
            sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
            
            // Refresh token dans localStorage (persistant)
            localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
            localStorage.setItem(REFRESH_EXPIRY_KEY, refreshExpiryTime.toString());
        }
        
        return response.data;
    } catch (error) {
        console.error('Erreur lors de la connexion:', error);
        throw error;
    }
}

export async function refreshAccessToken(): Promise<RefreshTokenResponse | null> {
    try {
        const refreshToken = getStoredRefreshToken();
        if (!refreshToken || isRefreshTokenExpired()) {
            throw new Error('Refresh token manquant ou expiré');
        }

        const refreshRequest: RefreshTokenRequest = { refreshToken };
        console.log('Envoi de la requête de refresh du token:', refreshRequest);
        const response = await post('/auth/refresh', refreshRequest);
        
        // Mettre à jour uniquement les tokens (pas l'utilisateur)
        if (response.data.accessToken) {
            const expiryTime = Date.now() + (response.data.expiresIn * 1000);
            
            // Access token dans sessionStorage (plus sécurisé)
            sessionStorage.setItem(ACCESS_TOKEN_KEY, response.data.accessToken);
            sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
            
            // Le refresh token reste le même (pas besoin de le mettre à jour)
            // Sauf si le serveur en renvoie un nouveau
            if (response.data.refreshToken) {
                localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
            }
        }
        
        return response.data;
    } catch (error) {
        console.error('Erreur lors du refresh du token:', error);
        // Si le refresh échoue, nettoyer les tokens
        clearStoredTokens();
        return null;
    }
}

export async function logout(): Promise<void> {
    try {
        const refreshToken = getStoredRefreshToken();
        if (refreshToken) {
            // Optionnel: appeler l'API pour invalider les tokens côté serveur
            await post('/auth/logout', { refreshToken });
        }
    } catch (error) {
        console.error('Erreur lors de la déconnexion:', error);
    } finally {
        // Nettoyer le localStorage dans tous les cas
        clearStoredTokens();
    }
}

export function getStoredAccessToken(): string | null {
    const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (token && !isAccessTokenExpired()) {
        return token;
    }
    return null;
}

export function getStoredRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Récupère les informations de l'utilisateur connecté depuis le backend
 */
export async function getCurrentUser(): Promise<User | null> {
    try {
        const token = getStoredAccessToken();
        if (!token) {
            return null;
        }
        
        const response = await get('/auth/me');
        return response.data;
    } catch (error) {
        console.error('Erreur lors de la récupération de l\'utilisateur:', error);
        return null;
    }
}

export function isAccessTokenExpired(): boolean {
    const expiryStr = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryStr) return true;
    
    const expiryTime = parseInt(expiryStr);
    const currentTime = Date.now();
    
    // Considérer comme expiré 30 secondes avant l'expiration réelle
    return currentTime >= (expiryTime - 30000);
}

export function isRefreshTokenExpired(): boolean {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const expiryStr = localStorage.getItem(REFRESH_EXPIRY_KEY);
    
    // Si pas de refresh token, ne pas considérer comme expiré (utilisateur non connecté)
    if (!refreshToken) return false;
    
    // Si refresh token existe mais pas d'expiry, considérer comme non expiré
    if (!expiryStr) return false;
    
    const expiryTime = parseInt(expiryStr);
    const currentTime = Date.now();
    
    return currentTime >= expiryTime;
}

export function clearStoredTokens(): void {
    // Nettoyer sessionStorage (access token, expiry)
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    
    // Nettoyer localStorage (refresh token et son expiry)
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_EXPIRY_KEY);
}

export async function verifyToken(): Promise<User | null> {
    try {
        const response = await get('/auth/verify');
        return response.data;
    } catch (error) {
        console.error('Token invalide:', error);
        // Nettoyer les tokens si la vérification échoue
        clearStoredTokens();
        return null;
    }
}

// Fonction utilitaire pour vérifier si on a besoin de rafraîchir le token
export function shouldRefreshToken(): boolean {
    const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    
    return !!accessToken && !!refreshToken && 
           isAccessTokenExpired() && !isRefreshTokenExpired();
}

// Fonction utilitaire pour vérifier si l'utilisateur peut être reconnecté automatiquement
export function canAutoReconnect(): boolean {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    return !!refreshToken && !isRefreshTokenExpired();
}